import {
	Fragment,
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	CommunityNode,
	CommunityRootNode,
	HubSidebarValue
} from "./HubSidebar";
import { TreeAccordion } from "client/Components";
import {
	MediaQueryKind,
	useToggle,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ExpandCollapseNode } from "./ExpandCollapseNode";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { HubDataSource } from "client/Api/Hub/hub";
import { ItemContent } from "./ItemContent";

export type CommunitiesSidebarSectionProps = {
	selectedValue: HubSidebarValue | undefined;
	rootNode: CommunityRootNode;
	defaultAmountOfItemsToDisplay: number;
	onSelect: (value: HubSidebarValue) => void;
};

export const CommunitiesSidebarSection = (props: CommunitiesSidebarSectionProps): ReactElement => {
	const {
		selectedValue,
		rootNode,
		defaultAmountOfItemsToDisplay,
		onSelect
	} = props;

	const [shouldShowAll, setShouldShowAll] = useState<boolean>(false);
	const [isCollapsed, onToggleCollapsed] = useToggle(false);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const { children } = rootNode;

	const childrenToDisplay = useMemo(
		(): readonly CommunityNode[] => {
			let result: readonly CommunityNode[] = children;
			if (!shouldShowAll) {
				result = result
					.slice(
						0,
						defaultAmountOfItemsToDisplay
					);
			}
			return result;
		},
		[
			defaultAmountOfItemsToDisplay,
			children,
			shouldShowAll
		]
	);

	useEffect(
		(): void => {
			if (selectedValue?.source !== HubDataSource.Community) {
				return;
			}

			onToggleCollapsed(false);

			setShouldShowAll(
				shouldShowAll => {
					return !shouldShowAll
						? children.some(
							(x, index) => x.id === selectedValue.communityId
								&& index >= defaultAmountOfItemsToDisplay
						)
						: shouldShowAll;
				}
			);
		},
		[
			children,
			defaultAmountOfItemsToDisplay,
			selectedValue,
			onToggleCollapsed
		]
	);

	const shouldRenderMoreButton: boolean = children.length > defaultAmountOfItemsToDisplay;

	return (
		<TreeAccordion.Node>
			<TreeAccordion.Item
				icon={rootNode.icon}
				expansionToggle="visible"
				isExpanded={!isCollapsed}
				onClick={onToggleCollapsed}
				onToggleExpansion={onToggleCollapsed}
				trackingId={`${tagPrefix}-node`}
				title={rootNode.name}
			>
				<ItemContent
					title={rootNode.name}
					newItemsIndicator={
						isCollapsed
							? rootNode.newItemsCount
							: undefined
					}
					isActive={false}
				/>
			</TreeAccordion.Item>

			<TreeAccordion.SubTree
				isCollapsed={isCollapsed}
				trackingId={`${tagPrefix}-sub-tree`}
			>
				{childrenToDisplay.map((x): ReactElement => {
					const isDiscover: boolean = isNil(x.id);
					return (
						<Fragment key={isDiscover ? "discover" : x.id}>
							<TreeAccordion.Node>
								<Item
									item={x}
									selectedValue={selectedValue}
									onSelect={onSelect}
								/>
							</TreeAccordion.Node>

							{isDiscover && !isMobile && (
								<TreeAccordion.Separator />
							)}
						</Fragment>
					);
				})}

				{shouldRenderMoreButton && (
					<ExpandCollapseNode
						isExpanded={shouldShowAll}
						onIsExpandedChange={setShouldShowAll}
						trackingId={`${tagPrefix}-expand`}
					/>
				)}
			</TreeAccordion.SubTree>
		</TreeAccordion.Node>
	);
};
CommunitiesSidebarSection.displayName = "CommunitiesSidebarSection";

const tagPrefix: string = "community";

type ItemProps = {
	item: CommunityNode;
	selectedValue: HubSidebarValue | undefined;
	onSelect: (value: HubSidebarValue) => void;
};

const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		selectedValue,
		onSelect
	} = props;

	const {
		id,
		name
	} = item;

	const clickHandler = useCallback(
		(): void => {
			onSelect({
				source: HubDataSource.Community,
				communityId: id
			});
		},
		[id, onSelect]
	);

	const isSelected: boolean = isNotNil(selectedValue)
		&& selectedValue.source === HubDataSource.Community
		&& selectedValue.communityId === id;

	return (
		<TreeAccordion.Item
			onClick={clickHandler}
			indentionLevel={1}
			trackingId={generateTrackingId(tagPrefix, `|${item.id ?? "discover"}`)}
			isSelected={isSelected}
			title={name}
		>
			<ItemContent
				title={name}
				newItemsIndicator={item.newItemsCount}
				isActive={isSelected}
			/>
		</TreeAccordion.Item>
	);
};
Item.displayName = "Item";
